import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { APIService } from 'src/api/api';

const initialState = {
  loading: false,
  countLoading: false,
  totalCount: 0,
  unreadCount: 0,
  notificationsList: [],
};

export const getNotificationsList = createAsyncThunk(
  'notifications/list',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getNotificationsList(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

export const loadMoreNotifications = createAsyncThunk(
  'notifications/list/loadmore',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getNotificationsList(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

export const getUnreadNotificationsCount = createAsyncThunk(
  'notifications/unread-count',
  async (_, { rejectWithValue }) => {
    try {
      const res = await APIService.getUnreadNotificationsCount();
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/mark-read',
  async (notificationId, { rejectWithValue }) => {
    try {
      const res = await APIService.markNotificationRead(notificationId);
      if (!res.success) throw res;
      return { notificationId, result: res.result };
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/mark-all-read',
  async (_, { rejectWithValue }) => {
    try {
      const res = await APIService.markAllNotificationsRead();
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

const markItemAsRead = (items, notificationId) => items.map((item) => {
  if (item.id !== notificationId || item.is_read) {
    return item;
  }

  return {
    ...item,
    is_read: true,
    read_on: new Date().toISOString(),
  };
});

const decrementUnreadCount = (count, changedItem) => (
  changedItem?.is_read ? count : Math.max(0, count - 1)
);

const markAllItemsAsRead = (items) => items.map((item) => (
  item.is_read
    ? item
    : {
      ...item,
      is_read: true,
      read_on: item.read_on || new Date().toISOString(),
    }
));

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getNotificationsList.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(getNotificationsList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        notificationsList: action.payload.results,
      }))
      .addCase(getNotificationsList.rejected, (state) => ({
        ...state,
        loading: false,
      }))
      .addCase(loadMoreNotifications.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(loadMoreNotifications.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        notificationsList: [...state.notificationsList, ...action.payload.results],
      }))
      .addCase(loadMoreNotifications.rejected, (state) => ({
        ...state,
        loading: false,
      }))
      .addCase(getUnreadNotificationsCount.pending, (state) => ({
        ...state,
        countLoading: true,
      }))
      .addCase(getUnreadNotificationsCount.fulfilled, (state, action) => ({
        ...state,
        countLoading: false,
        unreadCount: action.payload.count || 0,
      }))
      .addCase(getUnreadNotificationsCount.rejected, (state) => ({
        ...state,
        countLoading: false,
      }))
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const existingItem = state.notificationsList.find((item) => item.id === action.payload.notificationId);

        return {
          ...state,
          unreadCount: decrementUnreadCount(state.unreadCount, existingItem),
          notificationsList: markItemAsRead(state.notificationsList, action.payload.notificationId),
        };
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => ({
        ...state,
        unreadCount: 0,
        notificationsList: markAllItemsAsRead(state.notificationsList),
      }));
  }
});

export default notificationSlice.reducer;
